import { theme as antdTheme } from 'antd'
import type { ThemeConfig } from 'antd/es/config-provider/context'
import { ThemeProvider } from 'antd-style'
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

export type ThemeMode = 'light' | 'dark'
export type ThemePreference = 'system' | ThemeMode

const THEME_STORAGE_KEY = 'glows-theme-mode'

const LIGHT_ANTD_THEME: ThemeConfig = {
  algorithm: antdTheme.defaultAlgorithm,
  token: {
    colorPrimary: '#335CFF',
    colorBorder: '#99A0AE',
    colorBorderSecondary: '#EEEFF1',
    colorText: '#000000',
    colorTextSecondary: '#5A5F70',
    colorTextTertiary: '#99A0AE',
    colorBgLayout: '#F9FAFB',
    colorBgContainer: '#FFFFFF',
    colorBgElevated: '#FFFFFF',
    colorBgContainerDisabled: '#E1E4EA',
    colorFillSecondary: '#F5F7FA',
    borderRadius: 8,
  },
  components: {
    Table: {
      rowHoverBg: '#EBEFFD',
    },
  },
}

const DARK_ANTD_THEME: ThemeConfig = {
  algorithm: antdTheme.darkAlgorithm,
  token: {
    colorPrimary: '#4F6EF5',
    colorBorder: '#454E5E',
    colorBorderSecondary: '#2B313D',
    colorText: '#F5F7FA',
    colorTextSecondary: '#C4C6CE',
    colorTextTertiary: '#7B8394',
    colorBgLayout: '#0E121B',
    colorBgContainer: '#161A23',
    colorBgElevated: '#1F2430',
    colorBgContainerDisabled: '#343B48',
    colorFillSecondary: '#242A36',
    borderRadius: 8,
  },
  components: {
    Table: {
      rowHoverBg: '#1E2A4A',
    },
  },
}

interface ThemeContextValue {
  themePreference: ThemePreference
  themeMode: ThemeMode
  isDark: boolean
  isSystem: boolean
  setThemePreference: (themePreference: ThemePreference) => void
  setThemeMode: (themeMode: ThemeMode) => void
  toggleThemeMode: () => void
}

const ThemeModeContext = createContext<ThemeContextValue | null>(null)

function isThemePreference(value: string | null): value is ThemePreference {
  return value === 'system' || value === 'light' || value === 'dark'
}

function getSystemThemeMode(): ThemeMode {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function resolveThemeMode(
  themePreference: ThemePreference,
  systemThemeMode = getSystemThemeMode(),
) {
  return themePreference === 'system' ? systemThemeMode : themePreference
}

export function getInitialThemePreference(): ThemePreference {
  if (typeof window === 'undefined') return 'system'

  try {
    const storedPreference = window.localStorage.getItem(THEME_STORAGE_KEY)
    if (isThemePreference(storedPreference)) return storedPreference
  } catch {
    return 'system'
  }

  return 'system'
}

export function getInitialThemeMode(): ThemeMode {
  return resolveThemeMode(getInitialThemePreference())
}

export function applyThemeMode(themeMode: ThemeMode) {
  if (typeof document === 'undefined') return

  const root = document.documentElement
  root.classList.toggle('dark', themeMode === 'dark')
  root.dataset.theme = themeMode
}

function persistThemePreference(themePreference: ThemePreference) {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, themePreference)
  } catch {
    return
  }
}

function getAntdTheme(themeMode: ThemeMode): ThemeConfig {
  return themeMode === 'dark' ? DARK_ANTD_THEME : LIGHT_ANTD_THEME
}

export function AppThemeProvider({ children }: { children: ReactNode }) {
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(() =>
    getInitialThemePreference(),
  )
  const [systemThemeMode, setSystemThemeMode] = useState<ThemeMode>(() => getSystemThemeMode())
  const themeMode = resolveThemeMode(themePreference, systemThemeMode)

  useEffect(() => {
    applyThemeMode(themeMode)
    persistThemePreference(themePreference)
  }, [themeMode, themePreference])

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleSystemThemeChange = (event: MediaQueryListEvent) => {
      setSystemThemeMode(event.matches ? 'dark' : 'light')
    }

    setSystemThemeMode(mediaQuery.matches ? 'dark' : 'light')
    mediaQuery.addEventListener('change', handleSystemThemeChange)

    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange)
    }
  }, [])

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== THEME_STORAGE_KEY) return
      if (event.newValue === null) {
        setThemePreferenceState('system')
        return
      }
      if (!isThemePreference(event.newValue)) return
      setThemePreferenceState(event.newValue)
    }

    window.addEventListener('storage', handleStorage)
    return () => {
      window.removeEventListener('storage', handleStorage)
    }
  }, [])

  const setThemePreference = useCallback((nextThemePreference: ThemePreference) => {
    setThemePreferenceState(nextThemePreference)
  }, [])

  const setThemeMode = useCallback((nextThemeMode: ThemeMode) => {
    setThemePreferenceState(nextThemeMode)
  }, [])

  const toggleThemeMode = useCallback(() => {
    setThemePreferenceState(themeMode === 'dark' ? 'light' : 'dark')
  }, [themeMode])

  const contextValue = useMemo<ThemeContextValue>(
    () => ({
      themePreference,
      themeMode,
      isDark: themeMode === 'dark',
      isSystem: themePreference === 'system',
      setThemePreference,
      setThemeMode,
      toggleThemeMode,
    }),
    [setThemeMode, setThemePreference, themeMode, themePreference, toggleThemeMode],
  )

  const theme = useMemo(() => getAntdTheme(themeMode), [themeMode])

  return (
    <ThemeModeContext.Provider value={contextValue}>
      <ThemeProvider appearance={themeMode} theme={theme}>
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  )
}

export function useThemeMode() {
  const context = useContext(ThemeModeContext)
  if (!context) {
    throw new Error('useThemeMode must be used within AppThemeProvider')
  }
  return context
}
